import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router";
import { Box, Paper, Stack, Typography, Alert } from "@mui/material";
import { useForm } from "react-hook-form";
import { FormField, LoadingButton } from "@safrater/ui";
import { api } from "../../api/client";

interface Form { name: string; cpf: string; password: string; confirm: string }
interface InviteInfo { institutionName: string; email: string; name: string | null }

/**
 * Auto-registro público a partir de um convite (token na URL `?token=`).
 * Carrega os dados do convite, coleta nome/CPF/senha e conclui o ingresso
 * imediato na instituição. Mesmo padrão visual das demais telas públicas.
 */
export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const form = useForm<Form>({ defaultValues: { name: "", cpf: "", password: "", confirm: "" } });
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) { setLoadError("Link de convite inválido."); return; }
    (async () => {
      try {
        const res = await api.get<InviteInfo>(`/invites/${encodeURIComponent(token)}`);
        setInfo(res.data);
        if (res.data.name) form.setValue("name", res.data.name);
      } catch (e: unknown) {
        const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
        setLoadError(msg ?? "Convite inválido, expirado ou já utilizado.");
      }
    })();
  }, [token, form]);

  if (loadError) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 2 }}>
        <Paper sx={{ p: 4, maxWidth: 420 }}><Alert severity="error">{loadError}</Alert></Paper>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 2 }}>
        <Paper sx={{ p: 4, maxWidth: 420 }}>
          <Typography variant="h5" gutterBottom>Cadastro concluído</Typography>
          <Typography variant="body2">Você já faz parte de {info?.institutionName}. Entre com seu e-mail e senha.</Typography>
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <LoadingButton variant="contained" onClick={() => navigate("/login")}>Ir para o login</LoadingButton>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 440, width: "100%" }}>
        <Typography variant="h5" gutterBottom>Aceitar convite</Typography>
        {info && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Você foi convidado(a) para <strong>{info.institutionName}</strong> ({info.email}).
            Informe seus dados para concluir o ingresso.
          </Typography>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack
          component="form"
          spacing={2}
          onSubmit={form.handleSubmit(async (data) => {
            setError(null);
            if (data.password !== data.confirm) { setError("As senhas não conferem"); return; }
            try {
              await api.post("/invites/accept", {
                token, name: data.name.trim(), cpf: data.cpf.replace(/\D/g, ""), password: data.password,
              });
              setSuccess(true);
            } catch (e: unknown) {
              const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
              setError(msg ?? "Não foi possível concluir o cadastro.");
            }
          })}
        >
          <FormField
            label="Nome completo"
            {...form.register("name", { required: "Obrigatório", minLength: { value: 2, message: "Informe seu nome" } })}
            errorMessage={form.formState.errors.name?.message}
          />
          <FormField
            label="CPF"
            {...form.register("cpf", {
              required: "Obrigatório",
              validate: (v) => v.replace(/\D/g, "").length === 11 || "CPF inválido",
            })}
            errorMessage={form.formState.errors.cpf?.message}
          />
          <FormField
            type="password"
            label="Senha"
            {...form.register("password", { required: "Obrigatório", minLength: { value: 10, message: "Mínimo 10 caracteres" } })}
            errorMessage={form.formState.errors.password?.message}
            helperText="Use ao menos 10 caracteres."
          />
          <FormField
            type="password"
            label="Confirmar senha"
            {...form.register("confirm", { required: "Obrigatório" })}
            errorMessage={form.formState.errors.confirm?.message}
          />
          <LoadingButton type="submit" variant="contained" loading={form.formState.isSubmitting}>
            Concluir cadastro
          </LoadingButton>
        </Stack>
      </Paper>
    </Box>
  );
}
